const SESSION_STORAGE_KEY = 'ystnj_ai_conversation_task_launches';
const TASK_LAUNCH_RETENTION_MS = 30 * 60 * 1000;
const MATCH_FUZZ_MS = 5_000;

export type AssistantConversationTaskLaunchRecord = {
  threadId: string;
  runId: string | null;
  workItemId: string | null;
  workflowKey: string | null;
  title: string;
  route: string;
  launchedAt: number;
};

export type AssistantConversationTaskCompletionCandidate = {
  id: string;
  runId: string | null;
  workflowKey: string | null;
  title: string;
  route: string | null;
  updatedAt: number | null;
};

const normalizeText = (value: unknown): string => String(value ?? '').trim();

const canUseSessionStorage = (): boolean => (
  typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined'
);

const normalizeLaunchRecord = (
  value: unknown,
): AssistantConversationTaskLaunchRecord | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  const record = value as Record<string, unknown>;
  const threadId = normalizeText(record.threadId);
  const title = normalizeText(record.title);
  const route = normalizeText(record.route);
  const launchedAt = Number(record.launchedAt);
  if (!threadId || !title || !route || !Number.isFinite(launchedAt)) {
    return null;
  }
  return {
    threadId,
    runId: normalizeText(record.runId) || null,
    workItemId: normalizeText(record.workItemId) || null,
    workflowKey: normalizeText(record.workflowKey) || null,
    title,
    route,
    launchedAt,
  };
};

export const pruneAssistantConversationTaskLaunches = (
  launches: readonly AssistantConversationTaskLaunchRecord[],
  nowEpochMs = Date.now(),
): AssistantConversationTaskLaunchRecord[] => launches
  .filter((launch) => (nowEpochMs - launch.launchedAt) <= TASK_LAUNCH_RETENTION_MS)
  .sort((left, right) => right.launchedAt - left.launchedAt);

export const readAssistantConversationTaskLaunches = (): AssistantConversationTaskLaunchRecord[] => {
  if (!canUseSessionStorage()) {
    return [];
  }
  try {
    const raw = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) {
      return [];
    }
    const launches = parsed
      .map(normalizeLaunchRecord)
      .filter((launch): launch is AssistantConversationTaskLaunchRecord => Boolean(launch));
    return pruneAssistantConversationTaskLaunches(launches);
  } catch {
    return [];
  }
};

export const writeAssistantConversationTaskLaunches = (
  launches: readonly AssistantConversationTaskLaunchRecord[],
): void => {
  if (!canUseSessionStorage()) {
    return;
  }
  window.sessionStorage.setItem(
    SESSION_STORAGE_KEY,
    JSON.stringify(pruneAssistantConversationTaskLaunches(launches)),
  );
};

export const clearAssistantConversationTaskLaunches = (): void => {
  if (!canUseSessionStorage()) {
    return;
  }
  window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
};

export const upsertAssistantConversationTaskLaunch = (
  launches: readonly AssistantConversationTaskLaunchRecord[],
  nextLaunch: AssistantConversationTaskLaunchRecord,
  nowEpochMs = Date.now(),
): AssistantConversationTaskLaunchRecord[] => {
  const normalizedNext = normalizeLaunchRecord(nextLaunch);
  if (!normalizedNext) {
    return pruneAssistantConversationTaskLaunches(launches, nowEpochMs);
  }
  const retained = pruneAssistantConversationTaskLaunches(launches, nowEpochMs).filter((launch) => (
    !(
      (normalizedNext.runId && launch.runId === normalizedNext.runId)
      || (normalizedNext.workItemId && launch.workItemId === normalizedNext.workItemId)
      || launch.threadId === normalizedNext.threadId
      || (
        launch.workflowKey === normalizedNext.workflowKey
        && launch.title === normalizedNext.title
        && launch.route === normalizedNext.route
        && Math.abs(launch.launchedAt - normalizedNext.launchedAt) <= MATCH_FUZZ_MS
      )
    )
  ));
  retained.unshift(normalizedNext);
  return pruneAssistantConversationTaskLaunches(retained, nowEpochMs);
};

export const findAssistantConversationTaskLaunch = (
  task: AssistantConversationTaskCompletionCandidate,
  launches: readonly AssistantConversationTaskLaunchRecord[],
  nowEpochMs = Date.now(),
): AssistantConversationTaskLaunchRecord | null => {
  const normalizedTaskTitle = normalizeText(task.title);
  const normalizedTaskRoute = normalizeText(task.route);
  const normalizedTaskId = normalizeText(task.id);
  const normalizedTaskRunId = normalizeText(task.runId);
  const normalizedWorkflowKey = normalizeText(task.workflowKey);
  const updatedAt = typeof task.updatedAt === 'number' && Number.isFinite(task.updatedAt)
    ? task.updatedAt
    : null;
  if (!normalizedTaskTitle) {
    return null;
  }

  const activeLaunches = pruneAssistantConversationTaskLaunches(launches, nowEpochMs);
  for (const launch of activeLaunches) {
    if (launch.workItemId && normalizedTaskId && launch.workItemId === normalizedTaskId) {
      return launch;
    }
    if (launch.runId && normalizedTaskRunId && launch.runId === normalizedTaskRunId) {
      return launch;
    }
    if (!updatedAt) {
      continue;
    }
    if (launch.title !== normalizedTaskTitle) {
      continue;
    }
    if (launch.workflowKey && normalizedWorkflowKey && launch.workflowKey !== normalizedWorkflowKey) {
      continue;
    }
    if (normalizedTaskRoute && launch.route !== normalizedTaskRoute) {
      continue;
    }
    if (updatedAt < (launch.launchedAt - MATCH_FUZZ_MS)) {
      continue;
    }
    return launch;
  }
  return null;
};

export const removeAssistantConversationTaskLaunch = (
  launches: readonly AssistantConversationTaskLaunchRecord[],
  settledLaunch: AssistantConversationTaskLaunchRecord | null,
  nowEpochMs = Date.now(),
): AssistantConversationTaskLaunchRecord[] => {
  if (!settledLaunch) {
    return pruneAssistantConversationTaskLaunches(launches, nowEpochMs);
  }
  return pruneAssistantConversationTaskLaunches(launches, nowEpochMs).filter((launch) => !(
    launch.threadId === settledLaunch.threadId
    || (
      launch.launchedAt === settledLaunch.launchedAt
      && launch.title === settledLaunch.title
      && launch.route === settledLaunch.route
      && launch.runId === settledLaunch.runId
      && launch.workItemId === settledLaunch.workItemId
    )
  ));
};
